import * as React from "react";
import { useRouter } from "next/router";
import { css } from "@nju/css";
import { useOffers } from "@nju/data-access-portal-offers";
import { decodeToken } from "@nju/data-access-portal-wwt";
import { pushWWTFormSubmitEvent } from "@nju/data-layer";
import { Checkbox, ErrorMessage, Input } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import { Box, Button, Heading, Separator, Text } from "@nju/ui";
import { CityAutocomplete } from "./CityAutocomplete";
import { useWWTMachine } from "./machine";
import { StreetAutocomplete } from "./StreetAutocomplete";

interface IWWTPageProps {
  variant?: IScope;
}

export function WWTPage({ variant = "portal" }: IWWTPageProps) {
  const router = useRouter();
  const { indefiniteOffers } = useOffers().ftth;

  const [{ context, matches }, send] = useWWTMachine({
    variant,
    onPositiveVerification: ({ installationToken, address }) => {
      window.sessionStorage.setItem(
        "installationAddress",
        JSON.stringify(address),
      );
      window.sessionStorage.setItem("installationToken", installationToken);
      const decodedToken = decodeToken(installationToken);
      const offers = [...indefiniteOffers]
        .filter((item) => {
          return decodedToken?.limited
            ? item.limited === decodedToken.limited
            : true;
        })
        .map((item) => {
          return item.downloadSpeed;
        })
        .reverse()
        .join("/");

      pushWWTFormSubmitEvent(`${offers} Mb/s`, address.cityName);
      router.push(portalRoutes.wybierzOferte.get());
    },
    onNegativeVerification: ({ address, errorCode }) => {
      window.sessionStorage.setItem(
        "installationAddress",
        JSON.stringify(address),
      );
      pushWWTFormSubmitEvent("niedostępny", address.cityName);
      if (variant === "sales") {
        router.push(
          salesRoutes.sprawdzDostepnosc.uslugaNiedostepna.get({
            errorCode,
          }),
        );

        return;
      }

      router.push(portalRoutes.sprawdzDostepnosc.uslugaNiedostepna.get());
    },
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "verify" });
      },
      [send],
    );

  const isStreetDisabled = context.withoutStreet || !context.city;
  const isHouseNumberDisabled = context.withoutStreet
    ? !context.city
    : !context.street;

  return (
    <form className={styles.form()} onSubmit={handleSubmit}>
      <Heading
        className={styles.pageTitle()}
        font="ttfirs"
        tone="neutral"
        variant="h2"
        weight="bold"
      >
        gdzie chcesz podłączyć nju światłowód?
      </Heading>
      <div data-cy="city">
        <CityAutocomplete
          onChange={(value) => {
            send({ type: "setCity", value });
          }}
          selectedItem={context.city}
          state={context.cityError ? "error" : undefined}
        />
        <div data-cy="message-type-city">
          {context.cityError && <ErrorMessage message={context.cityError} />}
        </div>
      </div>

      <div data-cy="city-without-street-box">
        <Checkbox
          defaultSelected={context.withoutStreet}
          name="withoutStreet"
          onValueChange={(value) => {
            send({ type: "setWithoutStreet", value });
          }}
        >
          {variant === "portal" && "w mojej miejscowości nie ma nazw ulic"}
          {variant === "sales" && "w miejscowości klienta nie ma nazw ulic"}
        </Checkbox>
      </div>
      <div data-cy="streetName">
        <StreetAutocomplete
          cityId={context.city?.id}
          isDisabled={isStreetDisabled}
          onChange={(value) => {
            send({ type: "setStreet", value });
          }}
          state={!isStreetDisabled && context.streetError ? "error" : undefined}
        />
        {!isStreetDisabled && context.streetError && (
          <ErrorMessage message={context.streetError} />
        )}
      </div>

      <div className={styles.houseAndApartment()}>
        <div data-cy="house-number">
          <Input
            isDisabled={isHouseNumberDisabled}
            label="numer domu"
            name="houseNumber"
            onValueChange={(value) => {
              send({ type: "setHouseNumber", value });
            }}
            state={
              !isHouseNumberDisabled && context.houseNumberError
                ? "error"
                : undefined
            }
            value={context.houseNumber}
          />
          {!isHouseNumberDisabled && context.houseNumberError && (
            <ErrorMessage message={context.houseNumberError} />
          )}
        </div>
        <div data-cy="apartment-number">
          <Input
            isDisabled={context.withoutStreet ? !context.city : !context.street}
            label="numer lokalu"
            name="apartmentNumber"
            onValueChange={(value) => {
              send({ type: "setApartmentNumber", value });
            }}
            state={context.apartmentNumberError ? "error" : undefined}
            value={context.apartmentNumber}
          />

          {context.apartmentNumberError && (
            <ErrorMessage message={context.apartmentNumberError} />
          )}
        </div>
      </div>

      <Box
        align={{
          "@fromTablet": "center",
        }}
        data-cy="apartment-number-info"
        display="flex"
      >
        <Icon
          className={styles.icon()}
          height={20}
          name="info-circle"
          tone="accent"
          width={20}
        />
        <Text>
          {variant === "portal" &&
            "Jeśli Twój adres posiada numer lokalu, wpisz go, by wynik weryfikacji był prawidłowy."}
          {variant === "sales" &&
            "Jeśli adres posiada numer lokalu, wpisz go, by wynik weryfikacji był prawidłowy."}
        </Text>
      </Box>

      <div>
        <Separator tone="complementary" />
      </div>

      <div className={styles.buttons()}>
        <Button
          display={{ "@onlyMobile": "block", "@fromTablet": "flex" }}
          isLoading={!matches("ready")}
          type="submit"
        >
          sprawdź dostępność
        </Button>
      </div>
    </form>
  );
}

const styles = {
  form: css({
    display: "grid",
    gap: 20,
    pb: "$20",
    "@fromTablet": {
      pb: "$0",
    },
  }),
  pageTitle: css({
    "@fromDesktop": {
      letterSpacing: "-1.0px",
    },
  }),
  houseAndApartment: css({
    display: "grid",
    gap: 20,
    "@fromDesktop": {
      gridAutoFlow: "column",
    },
  }),
  title: css({
    paddingBottom: "$20",
    "@fromTablet": {
      paddingBottom: "$30",
    },
  }),
  field: css({
    paddingBottom: "$20",
  }),
  space: css({
    padding: "$10",
  }),
  separator: css({
    marginBottom: "$20",
    borderBottom: "1px solid",
    borderBottomColor: "$geyser",
    "@fromTablet": {
      marginBottom: "$30",
    },
  }),
  inputs: css({
    display: "flex",
    paddingBottom: "$20",
    flexDirection: "column",
    alignItems: "flex-start",
    "@fromTablet": {
      flexDirection: "row",
      paddingBottom: "$30",
    },
  }),
  houseNumberInput: css({
    width: "100%",
  }),
  buttons: css({
    display: "flex",
    "@fromTablet": {
      justifyContent: "flex-end",
    },
  }),
  warning: css({
    pt: "$10",
  }),
  submitError: css({
    pt: "$20",
  }),
  error: css({
    pt: "$5",
    "@fromTablet": {
      pt: "$10",
    },
  }),
  icon: css({
    flex: "0 0 20px",
    mr: "$10",
  }),
};
